/**
 * CreateEventAI — the "Build with AI" step (route create.ai).
 *
 * Flow (operator decision 2026-07-16, extended 2026-07-16 for iteration):
 * a prompt screen → generating → a REVIEW of the AI draft (summary + assumptions
 * + missing) that the user can **iterate on conversationally** — tell the AI to
 * add/change things, it refines the draft and asks for content it still needs —
 * all while in draft. "Use this draft" prefills the wizard and lands on Basics.
 *
 * No fake interactivity: every turn awaits aiAssist and only updates on resolve;
 * failures render the real error state (brief §9). Nothing persists here — the
 * draft prefills the wizard; the user saves through the normal Publish path.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { alpha, Btn, Card, Chip, Icon, T, tx } from "@v0y4ge/jrni-ui";
import { CreationShell } from "./CreationShell";
import { CreationStore } from "./store";
import { WizardCanvas } from "./wizard-utils";
import { useNav } from "../../../shell/router";
import { DataSourceStrip } from "../../../shell/opSource";
import { friendlyDateTime } from "../../../lib/datetime";
import {
  aiAssist, aiRefine, getAiProviderSettings, aiErrorCopy, providerDef,
  type AiAssistResult, type AiProviderSettings,
} from "../../ai/ai-api";
import { useT, t as i18nT } from "../../../lib/i18n";

const EXAMPLES = [
  "Beginner yoga workshop next Saturday 10–12, in-person, max 20 people",
  "Weekly product demo webinar every Wednesday at 4pm, 10 times, virtual",
  "First-time homebuyer seminar, evening, capacity 60, free registration",
  "VIP loyalty tasting evening, invite-only RSVP, max 40 guests",
];

interface ChatMsg { role: "user" | "assistant"; text: string; }

/** Fallback assistant line when a backend doesn't return `reply`. */
function summarize(res: AiAssistResult): string {
  const need = res.missing.filter((m) => m.requiredForPublish).map((m) => m.label);
  return need.length
    ? `I've updated the draft. To publish it I still need: ${need.join(", ")}. Tell me the details and I'll add them.`
    : "I've updated the draft. Everything required is filled — review it and use it when you're happy, or ask me to refine anything.";
}

function labelStyle(): React.CSSProperties {
  return { display: "block", fontSize: 13, fontWeight: 600, color: T.n1, marginBottom: 6 };
}

export default function CreateEventAI() {
  const t = useT();
  const nav = useNav();
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<AiAssistResult | null>(null);
  const [error, setError] = useState<ReturnType<typeof aiErrorCopy> | null>(null);
  const [settings, setSettings] = useState<AiProviderSettings | null>(null);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [turnInput, setTurnInput] = useState("");
  const composerRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    let live = true;
    getAiProviderSettings()
      .then((r) => { if (live) setSettings(r.settings); })
      .catch(() => { /* the assist call surfaces ai_not_configured with a real error */ });
    return () => { live = false; };
  }, []);

  const provider = settings?.status === "configured" ? providerDef(settings.provider) : null;

  const generate = async () => {
    const p = prompt.trim();
    if (!p || busy) return;
    setBusy(true); setError(null);
    try {
      const res = await aiAssist({ skill: "event-creation", prompt: p });
      setResult(res);
      setMessages([{ role: "user", text: p }, { role: "assistant", text: res.reply || summarize(res) }]);
    } catch (e) {
      setError(aiErrorCopy(e));
    } finally {
      setBusy(false);
    }
  };

  // Iterate: apply ONE change to the current draft via the dedicated aiRefine
  // endpoint (edits the draft in place — never regenerates from scratch).
  const sendTurn = async () => {
    const t = turnInput.trim();
    if (!t || busy || !result) return;
    const next = [...messages, { role: "user" as const, text: t }];
    setMessages(next); setTurnInput(""); setBusy(true); setError(null);
    const history = messages.slice(-12).map((m) => ({ role: m.role, content: m.text }));
    try {
      const res = await aiRefine({ skill: "event-creation", instruction: t, draft: result.draft, history });
      setResult(res);
      setMessages([...next, { role: "assistant", text: res.reply || summarize(res) }]);
    } catch (e) {
      const copy = aiErrorCopy(e);
      setError(copy);
      setMessages([...next, { role: "assistant", text: copy.detail }]);
    } finally {
      setBusy(false);
    }
  };

  const quickFill = (label: string) => {
    setTurnInput((v) => (v.trim() ? v : `Add the ${label.toLowerCase()}: `));
    setTimeout(() => composerRef.current?.focus(), 0);
  };

  const restart = () => { setResult(null); setMessages([]); setError(null); };

  const useDraft = () => {
    if (!result) return;
    CreationStore.prefillFromAiDraft(result.draft, {
      prompt: messages.filter((m) => m.role === "user").map((m) => m.text).join("\n") || prompt.trim(),
      assumptions: result.assumptions, missing: result.missing,
    });
    nav.go("create.basics");
  };

  const footer = result ? (
    <>
      <Btn variant="secondary" icon={<Icon.arrow dir="left" size={14} />} onClick={restart}>{t("events.creationCreateEventAI.startOver", "Start over")}</Btn>
      <Btn iconRight={<Icon.arrow size={14} />} onClick={useDraft}>{t("events.creationCreateEventAI.useThisDraft", "Use this draft")}</Btn>
    </>
  ) : (
    <>
      <Btn variant="secondary" icon={<Icon.arrow dir="left" size={14} />} onClick={() => nav.go("create.type")}>{t("common.actions.back", "Back")}</Btn>
      <Btn icon={<Icon.star size={14} />} disabled={busy || !prompt.trim()} onClick={() => void generate()}>
        {busy ? t("events.createEventAI.generating", "Generating…") : t("events.createEventAI.generateEvent", "Generate event")}
      </Btn>
    </>
  );

  return (
    <CreationShell step={0} footer={footer}>
      <WizardCanvas width="wide">
        <DataSourceStrip screen="create-ai" ops={["aiAssist", "aiRefine", "getAiProviderSettings"]} />

        <div style={{ marginBottom: 16, display: "flex", alignItems: "flex-start", gap: 12 }}>
          <span style={{ width: 40, height: 40, borderRadius: 10, background: alpha(T.p1, "18"), color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><Icon.star size={19} /></span>
          <div>
            <h2 style={{ ...tx.h2, fontWeight: 700 }}>{t("events.creationCreateEventAI.buildWithAi", "Build with AI")}</h2>
            <p style={{ ...tx.help, marginTop: 6 }}>
              {t("events.creationCreateEventAI.describeYourEventInPlainLanguageTheAiDraftsItThenYou", "Describe your event in plain language — the AI drafts it, then you can keep chatting to refine it before it's saved.")}{" "}{provider ? <> {t("events.creationCreateEventAI.using", "Using")}{" "}<strong style={{ color: T.n1 }}>{provider.name}</strong>{settings?.model ? <> · {settings.model}</> : null}.</> : null}
            </p>
          </div>
        </div>

        {!result ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 720 }}>
            <div>
              <label style={labelStyle()} htmlFor="ai-prompt">{t("events.creationCreateEventAI.describeTheEvent", "Describe the event")}</label>
              <textarea id="ai-prompt" value={prompt} onChange={(e) => { setPrompt(e.target.value); if (error) setError(null); }}
                placeholder={t("events.creationCreateEventAI.eGBeginnerYogaWorkshopNextSaturday1012InPersonAt", "e.g. Beginner yoga workshop next Saturday 10–12, in-person at the studio, max 20 people, free to attend")}
                rows={5} maxLength={4000}
                style={{ width: "100%", resize: "vertical", padding: "12px 14px", border: `1px solid ${T.n4}`, borderRadius: 8, fontFamily: T.font, fontSize: 14, lineHeight: 1.5, color: T.n1, background: T.white, outline: "none" }} />
              <div style={{ marginTop: 4, fontSize: 12, color: T.soft, textAlign: "right" }}>{prompt.length}/4000</div>
            </div>

            <div>
              <div style={{ fontSize: 12, color: T.soft, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.04, marginBottom: 8 }}>{t("events.creationCreateEventAI.tryAnExample", "Try an example")}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {EXAMPLES.map((ex) => (
                  <Chip key={ex} onClick={() => setPrompt(ex)} style={{ textAlign: "left", padding: "8px 12px", fontSize: 12.5, lineHeight: 1.35, border: `1px solid ${T.borderStrong}` }}>
                    {ex}
                  </Chip>
                ))}
              </div>
            </div>

            {error ? (
              <Card style={{ padding: 16, border: `1px solid ${alpha(T.red1, "44")}`, background: alpha(T.red1, "0a") }}>
                <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                  <span style={{ color: T.red1, flexShrink: 0, marginTop: 1 }}><Icon.warn size={18} /></span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.n1 }}>{error.title}</div>
                    <div style={{ fontSize: 13, color: T.n2, marginTop: 3, lineHeight: 1.5 }}>{error.detail}</div>
                    <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
                      {error.configure
                        ? <Btn size="sm" variant="secondary" icon={<Icon.settings size={13} />} onClick={() => nav.go("settings.ai")}>{t("events.creationCreateEventAI.openSettingsAi", "Open Settings → AI")}</Btn>
                        : <Btn size="sm" variant="secondary" icon={<Icon.refresh size={13} />} disabled={busy} onClick={() => void generate()}>{t("events.creationCreateEventAI.tryAgain", "Try again")}</Btn>}
                    </div>
                  </div>
                </div>
              </Card>
            ) : null}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) 400px", gap: 20, alignItems: "start" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <DraftCard result={result} />
              <AssumptionsMissing result={result} onQuickFill={quickFill} />
            </div>
            <ConversationPanel
              messages={messages} busy={busy} value={turnInput}
              onChange={setTurnInput} onSend={() => void sendTurn()} composerRef={composerRef}
            />
          </div>
        )}
      </WizardCanvas>
    </CreationShell>
  );
}

function DraftCard({ result }: { result: AiAssistResult }) {
  const t = useT();
  const d = result.draft;
  const tz = useMemo(() => (d.timezone ? String(d.timezone) : undefined), [d.timezone]);
  const fmt = d.format === "hybrid" ? "Hybrid" : d.format === "virtual" ? "Virtual" : "In-person";
  const dateLabel = d.startsAt ? (d.endsAt ? `${friendlyDateTime(String(d.startsAt), tz)} → ${friendlyDateTime(String(d.endsAt), tz)}` : friendlyDateTime(String(d.startsAt), tz)) : "Not set — add on the Schedule step";
  const ticketCount = (d.ticketTypeIds || []).length;

  const Field = ({ label, value }: { label: string; value: string }) => (
    <div style={{ display: "flex", gap: 12, padding: "9px 0", borderBottom: `1px solid ${T.n6}` }}>
      <div style={{ width: 120, flexShrink: 0, fontSize: 12, color: T.soft, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.03 }}>{label}</div>
      <div style={{ flex: 1, fontSize: 13.5, color: value ? T.n1 : T.soft, lineHeight: 1.5, wordBreak: "break-word" }}>{value || "—"}</div>
    </div>
  );

  return (
    <Card style={{ padding: 24 }}>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "4px 10px", borderRadius: 999, background: alpha(T.p1, "14"), color: T.p1, fontSize: 12, fontWeight: 700, marginBottom: 14 }}>
        <Icon.star size={12} /> {i18nT("events.creationCreateEventAI.aiDraftReviewRefineAmpEdit.rendered", "AI draft — review, refine & edit")}
      </div>
      <h3 style={{ fontSize: 19, fontWeight: 700, color: d.title ? T.n1 : T.soft, margin: "0 0 4px" }}>{d.title || "Untitled event"}</h3>
      {d.description ? <p style={{ fontSize: 13.5, color: T.n2, lineHeight: 1.6, margin: "0 0 12px" }}>{d.description}</p> : null}
      <Field label={t("events.creationCreateEventAI.format", "Format")} value={fmt} />
      <Field label={t("events.creationCreateEventAI.dateTime", "Date & time")} value={dateLabel} />
      {d.locationAddress || d.meetingLink ? <Field label={t("events.creationCreateEventAI.location", "Location")} value={String(d.locationAddress || d.meetingLink)} /> : null}
      <Field label={t("events.creationCreateEventAI.capacity", "Capacity")} value={d.capacity != null ? `${Number(d.capacity).toLocaleString()} seats` : "Not set"} />
      <Field label={t("events.creationCreateEventAI.tickets", "Tickets")} value={ticketCount ? `${ticketCount} preset ticket type${ticketCount === 1 ? "" : "s"}` : "None yet — add on the Tickets step"} />
      {d.repeatKind && d.repeatKind !== "none" ? <Field label={t("events.creationCreateEventAI.repeats", "Repeats")} value={String(d.repeatKind).replace(/-/g, " ")} /> : null}
      {(d.tags || []).length ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 12 }}>
          {(d.tags || []).map((t) => <span key={String(t)} style={{ fontSize: 12, padding: "3px 9px", borderRadius: 999, background: T.n6, color: T.n2 }}>{String(t)}</span>)}
        </div>
      ) : null}
    </Card>
  );
}

function AssumptionsMissing({ result, onQuickFill }: { result: AiAssistResult; onQuickFill: (label: string) => void }) {
  const t = useT();
  const requiredMissing = result.missing.filter((m) => m.requiredForPublish);
  const advisoryMissing = result.missing.filter((m) => !m.requiredForPublish);
  return (
    <>
      {result.assumptions.length ? (
        <Card style={{ padding: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.p1, textTransform: "uppercase", letterSpacing: 0.04, marginBottom: 10, display: "inline-flex", alignItems: "center", gap: 6 }}><Icon.info size={13} /> {t("events.creationCreateEventAI.whatTheAiAssumed", "What the AI assumed")}</div>
          <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8 }}>
            {result.assumptions.map((a, i) => (
              <li key={i} style={{ display: "flex", gap: 8, fontSize: 12.5, color: T.n1, lineHeight: 1.45 }}>
                <span style={{ marginTop: 6, width: 4, height: 4, borderRadius: 999, background: T.p1, flexShrink: 0 }} />{a}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {result.missing.length ? (
        <Card style={{ padding: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.n1, textTransform: "uppercase", letterSpacing: 0.04, marginBottom: 4, display: "inline-flex", alignItems: "center", gap: 6 }}><Icon.warn size={13} style={{ color: T.orange1 }} /> {t("events.creationCreateEventAI.addToCompleteIt", "Add to complete it")}</div>
          <div style={{ fontSize: 11.5, color: T.soft, marginBottom: 10 }}>{t("events.creationCreateEventAI.clickAnItemToAskTheAiToAddItOrFill", "Click an item to ask the AI to add it, or fill it later in the wizard.")}</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {requiredMissing.map((m) => (
              // ui-raw: transparent borderless clickable list row (required badge + multi-line reason); not a Btn
              <button key={m.field} type="button" onClick={() => onQuickFill(m.label)}
                style={{ display: "flex", gap: 8, alignItems: "flex-start", textAlign: "left", background: "transparent", border: "none", padding: 0, cursor: "pointer", fontFamily: T.font }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: T.orange1, background: alpha(T.orange1, "18"), borderRadius: 4, padding: "1px 6px", marginTop: 1, flexShrink: 0, textTransform: "uppercase" }}>{t("events.creationCreateEventAI.required", "Required")}</span>
                <div style={{ fontSize: 12.5, color: T.n1, lineHeight: 1.4 }}><strong>{m.label}</strong> — {m.reason}</div>
              </button>
            ))}
            {advisoryMissing.map((m) => (
              // ui-raw: transparent borderless clickable list row (bullet + multi-line reason); not a Btn
              <button key={m.field} type="button" onClick={() => onQuickFill(m.label)}
                style={{ display: "flex", gap: 8, alignItems: "flex-start", textAlign: "left", background: "transparent", border: "none", padding: 0, cursor: "pointer", fontFamily: T.font, color: T.n2 }}>
                <span style={{ marginTop: 6, width: 4, height: 4, borderRadius: 999, background: T.n4, flexShrink: 0 }} />
                <div style={{ fontSize: 12.5, lineHeight: 1.4 }}><strong style={{ color: T.n1 }}>{m.label}</strong> — {m.reason}</div>
              </button>
            ))}
          </div>
        </Card>
      ) : null}
    </>
  );
}

function ConversationPanel({ messages, busy, value, onChange, onSend, composerRef }: {
  messages: ChatMsg[]; busy: boolean; value: string;
  onChange: (v: string) => void; onSend: () => void;
  composerRef: React.RefObject<HTMLTextAreaElement | null>;
}) {
  const t = useT();
  const threadRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = threadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, busy]);

  return (
    <Card style={{ padding: 0, display: "flex", flexDirection: "column", position: "sticky", top: 8, height: "min(72vh, 620px)", overflow: "hidden" }}>
      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        <span style={{ color: T.p1, display: "inline-flex" }}><Icon.star size={15} /></span>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{t("events.creationCreateEventAI.iterateWithAi", "Iterate with AI")}</div>
        <span style={{ marginLeft: "auto", fontSize: 11.5, color: T.soft }}>{t("events.creationCreateEventAI.refinesTheDraftAbove", "Refines the draft above")}</span>
      </div>

      <div ref={threadRef} style={{ flex: 1, overflow: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{ maxWidth: "85%", padding: "9px 12px", borderRadius: 12, fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap", wordBreak: "break-word",
              background: m.role === "user" ? T.p1 : T.n6, color: m.role === "user" ? T.white : T.n1,
              borderBottomRightRadius: m.role === "user" ? 3 : 12, borderBottomLeftRadius: m.role === "user" ? 12 : 3 }}>
              {m.text}
            </div>
          </div>
        ))}
        {busy ? (
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div style={{ padding: "9px 12px", borderRadius: 12, background: T.n6, color: T.soft, fontSize: 13, display: "inline-flex", alignItems: "center", gap: 8 }}>
              <Icon.refresh size={13} /> {t("events.creationCreateEventAI.thinking", "Thinking…")}</div>
          </div>
        ) : null}
      </div>

      <div style={{ borderTop: `1px solid ${T.border}`, padding: 12, flexShrink: 0 }}>
        <textarea ref={composerRef} value={value} onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); } }}
          placeholder={t("events.creationCreateEventAI.askTheAiToChangeSomethingEGAddTwoTicketTiers", "Ask the AI to change something — e.g. 'add two ticket tiers: General $0 and VIP $50' or 'make the description shorter'")}
          rows={3} disabled={busy}
          style={{ width: "100%", resize: "none", padding: "10px 12px", border: `1px solid ${T.n4}`, borderRadius: 8, fontFamily: T.font, fontSize: 13, lineHeight: 1.5, color: T.n1, background: busy ? T.n6 : T.white, outline: "none" }} />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8 }}>
          <span style={{ fontSize: 11, color: T.soft }}>{t("events.creationCreateEventAI.enterToSendShiftEnterForANewLine", "Enter to send · Shift+Enter for a new line")}</span>
          <Btn size="sm" icon={<Icon.send size={13} />} disabled={busy || !value.trim()} onClick={onSend}>{t("common.actions.send", "Send")}</Btn>
        </div>
      </div>
    </Card>
  );
}
